// Libs
import { Box, Button, Grid2, Stack } from '@mui/material';
import classNames from 'classnames/bind';
import { useParams, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseMoreAction, BaseStatus, BaseTable, BaseToolbar, Loading, UnderDevelopment } from '~/components/common';
import { AdditionalInformation, BasicInformation } from '~/components/module/vendors';
import { SubrentalFormModal } from '~/components/module/subrental';
import { PurchaseOrderFormModal } from '~/components/module/purchaseOrder';
// Others
import { IBaseMoreAction, IHeaderCellType, ISortBy } from '~/utils/interface/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IQueryParamTrailer } from '~/utils/interface/trailer';
import { IPaginationResponse } from '~/utils/interface/base';
import { PermissionString } from '~/utils/interface/permissions';
import { IPurchaseOrder } from '~/utils/interface/purchaseOrder';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_TRAILER,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import { IQueryParamDetailVendor } from '~/utils/interface/vendors';
import { getDetailVendor } from '~/thunks/vendors/vendorsThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { PurchaseOrderEnum, TypeVendorDetailEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './SubrentalsVendorTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsPurchaseOrder = (
  t: TFunction,
  sortBy: ISortBy[],
  onView: (id: string, item: IPurchaseOrder) => void,
  onEdit: (id: string) => void,
  onArchive: (id: string) => void,
  permissions?: Record<PermissionString, boolean>
): IHeaderCellType<IPurchaseOrder>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_sub_table_name')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => (
        <Stack direction={'row'} alignItems={'center'}>
          <Box className={cx('textName')}>{record.vendorName}</Box>
        </Stack>
      ),
    },
    {
      id: 'poNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_sub_table_number')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.poNumber || EMPTY_VALUE}</Box>,
    },
    {
      id: 'vendor_detail_sub_table_needed_by',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_po_table_needed_by')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.neededByDate || EMPTY_VALUE}</Box>,
    },
    {
      id: 'orderId',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_sub_table_order')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <Box className={cx('textValue')}>{record?.orderId || EMPTY_VALUE}</Box>,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('vendor_detail_sub_table_status')}</Box>
        </Stack>
      ),
      render: (record: IPurchaseOrder) => <BaseStatus label={record?.status || EMPTY_STRING} />,
    },
    // {
    //   id: 'action',
    //   label: t('common_text_action', { ns: 'common/base' }),
    //   minWidth: 100,
    //   align: 'center',
    //   render: (record: IPurchaseOrder) => {
    //     const actions: IBaseMoreAction[] = [
    //       {
    //         label: t('table_action_view', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionView}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         onClick: () => onView(record.id, record),
    //       },
    //       {
    //         label: t('table_action_edit', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionEdit}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         onClick: () => onEdit(record.id),
    //       },
    //       {
    //         label: t('table_action_delete', { ns: 'common/base' }),
    //         icon: (
    //           <img
    //             src={icons.tableIconActionDelete}
    //             alt={t('common_alt_img', { ns: 'common/base' })}
    //             width={16}
    //             height={16}
    //           />
    //         ),
    //         hasOtherColor: true,
    //         onClick: () => onArchive(record.id),
    //       },
    //     ];
    //
    //     return <BaseMoreAction actions={actions} />;
    //   },
    // },
  ];
};

const SubrentalsVendorTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  const { t } = useTranslation(['admin/vendors', 'common/base']);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { id: vendorId } = useParams();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.vendor);
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshPurchaseOrderList } = useAppSelector((state) => state.purchaseOrder);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_TRAILER);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramDetail] = useState<IQueryParamDetailVendor>({
    vendorId: vendorId || EMPTY_STRING,
    type: TypeVendorDetailEnum.SUB_RENTAL,
  });

  const [paramObject, setParamObject] = useState<IQueryParamTrailer>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [totalItems, setTotalItems] = useState<number>(0);

  const [subRentalList, setSubRentalList] = useState<IPurchaseOrder[]>([]);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isOpenSubrentalModal, setIsOpenSubrentalModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!vendorId) return;

    handleGetDetailVendor(paramDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  useEffect(() => {
    if (!isRefreshPurchaseOrderList || !vendorId) return;

    handleGetDetailVendor(paramDetail);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshPurchaseOrderList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleView = (id: string) => {};

  const handleEdit = (id: string) => {
    handleShowUnderDevelopment();
  };

  const handleArchive = (id: string) => {
    handleShowUnderDevelopment();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleGetDetailVendor = (params: IQueryParamDetailVendor) => {
    dispatch(getDetailVendor(params))
      .unwrap()
      .then((res) => {
        const { purchaseOrders, pagination } = res?.data;
        const totalItems = Number(pagination?.totalItems ?? 0);

        if (!purchaseOrders) return;

        setSubRentalList(purchaseOrders);
        setPagination(pagination);
        setTotalItems(totalItems);
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleAddSubrental = () => {
    setIsOpenSubrentalModal(!isOpenSubrentalModal);
  };

  const handleCloseSubrentalModal = () => {
    setIsOpenSubrentalModal(false);
  };
  //#endregion Handle Function

  return (
    <>
      <Box id='infoVendorTabComponent' className={cx('container')}>
        <Grid2 container spacing={6} className={cx('itemBox')}>
          <BaseToolbar title={t('vendor_detail_sub_page_title')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAddSubrental}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('vendor_detail_sub_button_add')}
            </Button>
          </BaseToolbar>

          <Grid2 size={6}>
            <BasicInformation />
          </Grid2>

          <Grid2 size={6}>
            <AdditionalInformation />
          </Grid2>
        </Grid2>

        <Box component={'div'} className={cx('vendorTable')}>
          <BaseTable
            dataSource={subRentalList}
            currentPage={pageSelected}
            totalPage={pagination?.totalPages}
            columns={columnsPurchaseOrder(t, sortBy, handleView, handleEdit, handleArchive)}
            onPageChange={handlePageChange}
            onRowClick={handleView}
          />
        </Box>

        {isLoading && <Loading />}

        {/* {isOpenSubrentalModal && <SubrentalFormModal vendorId={vendorId} onClose={handleCloseSubrentalModal} />} */}

        {isOpenSubrentalModal && (
          <PurchaseOrderFormModal
            vendorId={vendorId}
            onClose={handleCloseSubrentalModal}
            type={PurchaseOrderEnum.SUB_RENTAL}
          />
        )}

        {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
      </Box>
    </>
  );
};

export default SubrentalsVendorTab;
